import { create } from 'zustand';

type EntityType = 'trips' | 'drivers';
type ViewMode = 'board' | 'map' | 'timeline';
type DriverFilter = 'all' | 'unassigned' | 'assigned';

// All possible board column keys
export const ALL_BOARD_COLUMNS = [
  'available',
  'dispatched',
  'en_route_pickup',
  'at_pickup',
  'loading',
  'in_transit',
  'at_delivery',
  'unloading',
  'delivered',
  'cancelled',
] as const;

export type BoardColumnKey = typeof ALL_BOARD_COLUMNS[number];

// Default visible columns (all except cancelled)
const DEFAULT_VISIBLE_COLUMNS: BoardColumnKey[] = [
  'available',
  'dispatched',
  'en_route_pickup',
  'at_pickup',
  'loading',
  'in_transit',
  'at_delivery',
  'unloading',
  'delivered',
];

interface DispatchState {
  // Entity and view
  entityType: EntityType;
  viewMode: ViewMode;

  // Filters
  driverFilter: DriverFilter;
  driverSearchQuery: string;
  tripsSearchQuery: string;

  // Board column visibility
  visibleColumns: BoardColumnKey[];
}

interface DispatchActions {
  setEntityType: (type: EntityType) => void;
  setViewMode: (mode: ViewMode) => void;
  setDriverFilter: (filter: DriverFilter) => void;
  setDriverSearchQuery: (query: string) => void;
  setTripsSearchQuery: (query: string) => void;
  setVisibleColumns: (columns: BoardColumnKey[]) => void;
  toggleColumn: (column: BoardColumnKey) => void;
  resetAll: () => void;
}

type DispatchStore = DispatchState & DispatchActions;

const initialState: DispatchState = {
  entityType: 'trips',
  viewMode: 'board',
  driverFilter: 'all',
  driverSearchQuery: '',
  tripsSearchQuery: '',
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
};

export const useDispatchStore = create<DispatchStore>()((set) => ({
  ...initialState,

  setEntityType: (entityType) => set({ entityType }),
  setViewMode: (viewMode) => set({ viewMode }),
  setDriverFilter: (driverFilter) => set({ driverFilter }),
  setDriverSearchQuery: (driverSearchQuery) => set({ driverSearchQuery }),
  setTripsSearchQuery: (tripsSearchQuery) => set({ tripsSearchQuery }),
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),
  resetAll: () => set(initialState),
}));
